import { getFormatMaxDecimals } from "@src/shared/helpers/number";
import { isNullish } from "radashi";
import { PhysicalType } from "../units/constants";
import { utilsRepository } from "../utils/utilsRepository";
import {
  FieldDefinition,
  isFieldDefinition,
} from "./fieldDefinitions/definitions";
import { isNumberDefinition } from "./fieldDefinitions/getNumberDefinition";

export type NormalizeValueOptions = {
  conversionFn?: (value: number, unitType: PhysicalType) => number;
  range?: { max: number; min: number };
  round?: "ceil" | "floor" | boolean;
};

export function processNumericValue<TValue>(
  value: TValue,
  definition:
    | {
        maxDecimal?: number | null;
        physicalType?: PhysicalType | null;
      }
    | FieldDefinition
    | null,
  { conversionFn, range, round }: NormalizeValueOptions = {},
) {
  if (
    !definition ||
    typeof value !== "number" ||
    (isFieldDefinition(definition) && !isNumberDefinition(definition))
  ) {
    return value;
  }

  const { max, min } = range || {
    max: Number.POSITIVE_INFINITY,
    min: Number.NEGATIVE_INFINITY,
  };
  const converted =
    conversionFn && definition.physicalType
      ? conversionFn(value, definition.physicalType)
      : value;

  const result = roundValue(converted, definition.maxDecimal, round);
  return Math.min(Math.max(result, min), max);
}

function roundValue(
  value: number,
  maxDecimal?: number | null,
  round?: "ceil" | "floor" | boolean,
) {
  if (isNullish(maxDecimal) || isNullish(round) || round === false)
    return value;
  const formatDecimals = getFormatMaxDecimals(value, maxDecimal);
  const factor = Math.pow(10, formatDecimals);
  if (round === "ceil") return Math.ceil(value * factor) / factor;
  if (round === "floor") return Math.floor(value * factor) / factor;
  return utilsRepository.roundNumber(value, formatDecimals);
}
